import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";
import { errMsg } from "../../../lib/xutil";
import { Field, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";

export const StatusBadge = ({ active, invited, testId }) => {
  const { t } = useI18n();
  const cls = !active ? "bg-slate-200 text-slate-600" : invited ? "bg-sky-100 text-sky-800" : "bg-brand/25 text-navy";
  return <span data-testid={testId} className={`inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ${cls}`}>{t(!active ? "cstat.inactive" : invited ? "cstat.invited" : "cstat.active")}</span>;
};

const ReasonFields = ({ active, reason, setReason, resend, setResend }) => {
  const { t } = useI18n();
  return (
    <>
      <Field label={t("cstat.reason")}><textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} className={`${input} h-auto py-2`} data-testid="status-reason" /></Field>
      {active && <label className="flex min-h-11 items-center gap-3 text-sm text-navy"><input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} className="h-5 w-5 accent-[#002068]" data-testid="status-resend" />{t("cstat.resend")}</label>}
    </>
  );
};

// Confirm one target state for one or more customer codes.
export const StatusDialog = ({ codes, active, onClose, onDone }) => {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const body = { active, reason: reason.trim() || null, resend_invites: resend };
      const { data } = codes.length === 1
        ? await api.post(`/staff/customers/${encodeURIComponent(codes[0])}/status`, body).then((r) => ({ data: { changed: r.data.changed ? 1 : 0 } }))
        : await api.post("/staff/customers/status/bulk", { ...body, codes });
      toast.success(t("cstat.done", { n: data.changed }));
      onDone();
    } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="status-dialog">
        <DialogHeader><DialogTitle>{codes.length === 1 ? t(active ? "cstat.actOne" : "cstat.deactOne") : t(active ? "cstat.actTitle" : "cstat.deactTitle", { n: codes.length })}</DialogTitle>
          <DialogDescription>{t(active ? "cstat.actBody" : "cstat.deactBody")}</DialogDescription></DialogHeader>
        <ReasonFields active={active} reason={reason} setReason={setReason} resend={resend} setResend={setResend} />
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className={btnGhost} data-testid="status-cancel">{t("ops.cancel")}</button>
          <button type="button" onClick={go} disabled={busy} className={`${btnPrimary} ${active ? "" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="status-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t(active ? "cstat.activate" : "cstat.deactivate")}</button></div>
      </DialogContent>
    </Dialog>
  );
};

const Sel = ({ value, onChange, options, testId }) => {
  const { t } = useI18n();
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testId}><option value="">{t("cstat.any")}</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
};

export const BulkDialog = ({ meta, onClose, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ area: "", sales_rep: "", no_orders_months: "", status: "all" });
  const [active, setActive] = useState(false);
  const [reason, setReason] = useState("");
  const [resend, setResend] = useState(false);
  const [matched, setMatched] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMatched(null); }, [f, active]);
  const body = (dry) => ({ active, reason: reason.trim() || null, resend_invites: resend, dry_run: dry,
    filter: { area: f.area || null, sales_rep: f.sales_rep || null, status: f.status, no_orders_months: f.no_orders_months ? Number(f.no_orders_months) : null } });
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/customers/status/bulk", body(dry));
      if (dry) setMatched(data.matched); else { toast.success(t("cstat.done", { n: data.changed })); onDone(); }
    } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-lg" data-testid="bulk-status-dialog">
        <DialogHeader><DialogTitle>{t("cstat.bulk")}</DialogTitle><DialogDescription>{t(active ? "cstat.actBody" : "cstat.deactBody")}</DialogDescription></DialogHeader>
        <div className="flex gap-2">{[false, true].map((a) => <button key={String(a)} type="button" className={chip(active === a)} onClick={() => setActive(a)} data-testid={`bulk-action-${a ? "activate" : "deactivate"}`}>{t(a ? "cstat.activate" : "cstat.deactivate")}</button>)}</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("cstat.area")}><Sel value={f.area} onChange={set("area")} options={meta.areas || []} testId="bulk-area" /></Field>
          <Field label={t("cstat.rep")}><Sel value={f.sales_rep} onChange={set("sales_rep")} options={meta.reps || []} testId="bulk-rep" /></Field>
          <Field label={t("cstat.noOrders")}><input inputMode="numeric" value={f.no_orders_months} onChange={(e) => set("no_orders_months")(e.target.value.replace(/\D/g, "").slice(0, 2))} className={input} data-testid="bulk-months" /></Field>
          <Field label={t("cstat.statusLabel")}><select value={f.status} onChange={(e) => set("status")(e.target.value)} className={input} data-testid="bulk-status-filter">{["all", "active", "inactive"].map((k) => <option key={k} value={k}>{t(`cstat.${k}`)}</option>)}</select></Field>
        </div>
        <ReasonFields active={active} reason={reason} setReason={setReason} resend={resend} setResend={setResend} />
        {matched !== null && <p className="rounded-xl bg-canvas px-3 py-2 text-sm font-semibold text-navy" data-testid="bulk-matched">{t("cstat.matched", { n: matched })}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => run(true)} disabled={busy} className={btnGhost} data-testid="bulk-preview">{t("cstat.preview")}</button>
          <button type="button" onClick={() => run(false)} disabled={busy || !matched} className={`${btnPrimary} ${active ? "" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="bulk-apply">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("cstat.apply", { n: matched || 0 })}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const StatusHistory = ({ rows }) => {
  const { t } = useI18n();
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="status-history-empty">{t("cstat.noHistory")}</p>;
  return (
    <ul className="divide-y divide-slate-100 text-sm" data-testid="status-history">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
          <span className={`font-semibold ${r.action.endsWith("deactivate") ? "text-rose-700" : "text-navy"}`}>{t(`cstat.act.${r.action}`)}</span>
          {r.user_email && <span className="text-slate-600">{r.user_email}</span>}
          <span className="text-xs text-slate-500">{fmtDateTime(r.at)} · {r.by_name}</span>
          {r.reason && <span className="w-full text-xs text-slate-600">«{r.reason}»</span>}
        </li>))}
    </ul>
  );
};
